import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { Warning, Lightning, Coins, Microphone, ArrowRight, ArrowUp } from '@phosphor-icons/react';

/**
 * UsageEnforcement — Shared hooks and components for credit/voice enforcement.
 */

// ═══════════════════════════════════════════
// Hook: useUsageStatus
// ═══════════════════════════════════════════

export const useUsageStatus = () => {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id || '';
  const [creditStatus, setCreditStatus] = useState(null);
  const [voiceStatus, setVoiceStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!orgId) return;
    try {
      const headers = { 'x-org-id': orgId };
      const [creditRes, voiceRes] = await Promise.all([
        fetchWithOrg(`${BACKEND_URL}/api/pricing/balance`, { headers }),
        fetchWithOrg(`${BACKEND_URL}/api/pricing/voice`, { headers }),
      ]);
      if (creditRes.ok) setCreditStatus(await creditRes.json());
      if (voiceRes.ok) setVoiceStatus(await voiceRes.json());
    } catch (e) {
      console.warn('Usage status fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, [refresh]);

  return { creditStatus, voiceStatus, loading, refresh };
};


// ═══════════════════════════════════════════
// Credit Warning Banner
// ═══════════════════════════════════════════

export const CreditWarningBanner = ({ creditStatus, onAddCredits }) => {
  if (!creditStatus || !creditStatus.warning) return null;

  const isExhausted = creditStatus.warning === 'exhausted';
  const bgColor = isExhausted ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)';
  const borderColor = isExhausted ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.12)';
  const textColor = isExhausted ? '#EF4444' : '#F59E0B';
  const Icon = isExhausted ? Warning : Lightning;

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm"
      style={{ background: bgColor, border: `1px solid ${borderColor}` }}
      data-testid="credit-warning-banner"
    >
      <Icon size={16} style={{ color: textColor }} weight="fill" />
      <span className="flex-1 text-[#F0F4F8] text-xs">
        {creditStatus.message}
        {creditStatus.balance > 0 && (
          <span className="text-[#7D8896] ml-1">({creditStatus.balance} remaining)</span>
        )}
      </span>
      {creditStatus.cta && (
        <button
          onClick={onAddCredits}
          className="text-xs font-medium px-3 py-1 rounded-lg transition-colors flex items-center gap-1.5"
          style={{ color: textColor, background: `${textColor}15` }}
          data-testid="credit-add-btn"
        >
          {creditStatus.cta}
          <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════
// Voice Warning Banner
// ═══════════════════════════════════════════

export const VoiceWarningBanner = ({ voiceStatus, onBuyMinutes }) => {
  if (!voiceStatus || !voiceStatus.blocked) return null;

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm"
      style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}
      data-testid="voice-warning-banner"
    >
      <Microphone size={16} className="text-[#EF4444]" weight="fill" />
      <span className="flex-1 text-[#F0F4F8] text-xs">{voiceStatus.message}</span>
      {voiceStatus.cta && (
        <button
          onClick={onBuyMinutes}
          className="text-xs font-medium px-3 py-1 rounded-lg transition-colors flex items-center gap-1.5 text-[#EF4444] bg-[rgba(239,68,68,0.08)]"
          data-testid="voice-buy-btn"
        >
          {voiceStatus.cta}
          <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════
// Blocked Execution Modal
// ═══════════════════════════════════════════

export const BlockedModal = ({ type = 'credits', message, onAction, onClose }) => {
  if (!message) return null;

  const isCredits = type === 'credits';
  const Icon = isCredits ? Coins : Microphone;
  const accentColor = '#EF4444';
  const cta = isCredits ? 'Add credits' : 'Buy more minutes';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="blocked-modal-overlay"
    >
      <div
        className="bg-[#111827] border border-[rgba(255,255,255,0.08)] rounded-2xl p-8 max-w-sm w-full mx-4 text-center"
        onClick={e => e.stopPropagation()}
        data-testid="blocked-modal"
      >
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{ background: `${accentColor}10` }}
        >
          <Icon size={28} style={{ color: accentColor }} weight="duotone" />
        </div>
        <h3 className="text-lg font-semibold text-[#F0F4F8] mb-2">{message}</h3>
        <p className="text-sm text-[#7D8896] mb-6">
          {isCredits
            ? 'Top up your credits to keep your workers running.'
            : 'Purchase more voice minutes to continue using voice features.'}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm text-[#B0B8C4] border border-[rgba(255,255,255,0.08)] rounded-xl hover:bg-[rgba(255,255,255,0.04)] transition-colors"
            data-testid="blocked-modal-dismiss"
          >
            Dismiss
          </button>
          <button
            onClick={onAction}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors"
            style={{ background: '#00E5C0' }}
            data-testid="blocked-modal-action"
          >
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════
// Compact Credit Pill (for headers/sidebars)
// ═══════════════════════════════════════════

export const CreditPill = ({ creditStatus, onClick }) => {
  if (!creditStatus) return null;

  const balance = creditStatus.balance ?? 0;
  const warning = creditStatus.warning;

  let color = '#7D8896';
  if (warning === 'exhausted') color = '#EF4444';
  else if (warning === 'critical' || warning === 'low') color = '#F59E0B';
  else if (balance > 0) color = '#00E5C0';

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all hover:bg-[rgba(255,255,255,0.04)]"
      style={{ color, border: `1px solid ${color}25` }}
      title={`${balance} credits remaining`}
      data-testid="credit-pill"
    >
      <Coins size={13} weight="fill" />
      <span>{balance.toLocaleString()}</span>
    </button>
  );
};


// ═══════════════════════════════════════════
// Usage Widget — Sidebar credits + voice
// ═══════════════════════════════════════════

const getStatusStyle = (percent, blocked) => {
  if (blocked || percent <= 0) return { bar: '#EF4444', text: '#EF4444', label: 'Empty' };
  if (percent <= 30) return { bar: '#F59E0B', text: '#F59E0B', label: 'Low' };
  return { bar: '#00E5C0', text: '#00E5C0', label: '' };
};

export const UsageWidget = ({ creditStatus, voiceStatus, expanded, onTopUp }) => {
  if (!creditStatus) return null;

  const balance = creditStatus.balance ?? 0;
  const allowance = creditStatus.monthly_allowance ?? 100;
  const creditPercent = allowance > 0 ? Math.round((balance / allowance) * 100) : 0;
  const creditStyle = getStatusStyle(creditPercent, creditStatus.blocked);

  const voiceUsed = voiceStatus?.minutes_used ?? 0;
  const voiceLimit = voiceStatus?.minutes_limit ?? 0;
  const voiceRemaining = voiceStatus?.minutes_remaining ?? 0;
  const voicePercent = voiceLimit > 0 ? Math.round((voiceRemaining / voiceLimit) * 100) : 0;
  const voiceStyle = getStatusStyle(voicePercent, voiceStatus?.blocked);
  const hasVoice = voiceLimit > 0;

  if (!expanded) {
    return (
      <button
        onClick={onTopUp}
        className="w-full flex flex-col items-center gap-1 p-2 rounded-lg bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
        title={`${balance.toLocaleString()} credits remaining`}
        data-testid="usage-widget-collapsed"
      >
        <Lightning size={14} weight="duotone" style={{ color: creditStyle.bar }} />
        <span className="text-[10px] font-semibold" style={{ color: creditStyle.text }}>
          {balance.toLocaleString()}
        </span>
      </button>
    );
  }

  return (
    <div
      className="rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] p-3 space-y-3"
      data-testid="usage-widget"
    >
      {/* Credits */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <Lightning size={12} weight="fill" style={{ color: creditStyle.bar }} />
            <span className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">Credits</span>
          </div>
          <span className="text-[10px] font-medium" style={{ color: creditStyle.text }}>
            {balance.toLocaleString()}{' '}
            <span className="text-[#4B5563]">/ {allowance.toLocaleString()}</span>
          </span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-[rgba(255,255,255,0.06)] overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, creditPercent)}%`, backgroundColor: creditStyle.bar }}
          />
        </div>
        {creditStatus.warning && (
          <p className="text-[10px] mt-1" style={{ color: creditStyle.text }}>
            {creditStatus.message}
          </p>
        )}
      </div>

      {/* Voice */}
      {hasVoice && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <Microphone size={12} weight="fill" style={{ color: voiceStyle.bar }} />
              <span className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">Voice</span>
            </div>
            <span className="text-[10px] font-medium" style={{ color: voiceStyle.text }}>
              {Math.round(voiceRemaining)}{' '}
              <span className="text-[#4B5563]">/ {voiceLimit} min</span>
            </span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-[rgba(255,255,255,0.06)] overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, voicePercent)}%`, backgroundColor: voiceStyle.bar }}
            />
          </div>
          {voiceStatus?.blocked && (
            <p className="text-[10px] mt-1" style={{ color: voiceStyle.text }}>
              {voiceStatus.message}
            </p>
          )}
        </div>
      )}

      {/* Top Up Button */}
      <button
        onClick={onTopUp}
        className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-semibold text-[#00E5C0] bg-[rgba(0,229,192,0.06)] hover:bg-[rgba(0,229,192,0.1)] transition-colors"
        data-testid="usage-widget-topup-btn"
      >
        Top up
      </button>
    </div>
  );
};


// ═══════════════════════════════════════════
// Inline Credit Estimate — "This will use ~X credits"
// ═══════════════════════════════════════════

export const CreditEstimate = ({ credits, className = '' }) => {
  if (!credits || credits <= 0) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] text-[#7D8896] ${className}`}
      data-testid="credit-estimate"
    >
      <Lightning size={11} weight="fill" className="text-[#7D8896]" />
      ~{credits} credits
    </span>
  );
};


// ═══════════════════════════════════════════
// Upgrade Prompt — Suggests upgrade for power users
// ═══════════════════════════════════════════

export const UpgradePrompt = ({ reason, currentPlan, onUpgrade, onDismiss }) => {
  if (!reason) return null;

  const messages = {
    high_usage: "You're using credits fast. A higher plan gives you more each month.",
    multi_user: 'Need your team on board? Teams and Partner Pro support multiple users.',
    advanced_features: 'Unlock advanced features like Orion with a Professional plan or above.',
  };

  const msg = messages[reason] || messages.high_usage;

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-xl bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.1)]"
      data-testid="upgrade-prompt"
    >
      <ArrowUp size={16} className="text-[#00E5C0] mt-0.5 shrink-0" weight="bold" />
      <div className="flex-1">
        <p className="text-xs text-[#C0C8D4]">{msg}</p>
        <div className="flex items-center gap-3 mt-2">
          <button
            onClick={onUpgrade}
            className="text-xs font-semibold text-[#00E5C0] hover:underline"
            data-testid="upgrade-prompt-action"
          >
            View plans
          </button>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-xs text-[#4B5563] hover:text-[#7D8896]"
              data-testid="upgrade-prompt-dismiss"
            >
              Not now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
